import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'localization';
import type { StatusValue } from 'types/spherePoints';
import type { VelocityBucketKey } from 'types/sphereMetrics';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import TextareaAtom from '../../atoms/Textarea/TextareaAtom';
import SegmentedControlAtom, {
  type SegmentedControlOption,
} from '../../atoms/SegmentedControl/SegmentedControlAtom';
import {
  VELOCITY_BUCKET_COLORS,
  VELOCITY_BUCKET_LABEL_KEYS,
} from './checkInConstants';
import { STATUS_CSS_VARS } from '../../../utils/statusColors';
import './CheckInFormContent.scss';

const STATUS_VALUES: StatusValue[] = [
  'on_track',
  'at_risk',
  'needs_attention',
  'critical',
  'blocked',
];

export type { StatusValue } from 'types/spherePoints';

export interface StatusOption {
  value: StatusValue;
  label: string;
  color: string;
}

export interface CheckInFormContentProps {
  commentRequired?: boolean;
  minCommentLength?: number;
  velocityScore?: number;
  velocityBucket?: VelocityBucketKey;
  /** Pre-select the current status value (e.g. the sphere's current factor status) */
  defaultStatus?: StatusValue;
  isSubmitting?: boolean;
  error?: string | null;
  onSubmit: (statusValue: StatusValue, comment?: string) => void;
  onCancel: () => void;
  className?: string;
}

const CheckInFormContent: React.FC<CheckInFormContentProps> = ({
  commentRequired = false,
  minCommentLength = 0,
  velocityScore,
  velocityBucket,
  defaultStatus,
  isSubmitting = false,
  error,
  onSubmit,
  onCancel,
  className = '',
}) => {
  const { t } = useTranslation();
  const [selectedStatus, setSelectedStatus] = useState<StatusValue | null>(defaultStatus ?? null);
  const [comment, setComment] = useState('');
  const [statusError, setStatusError] = useState('');
  const [commentError, setCommentError] = useState('');

  const statusOptions: StatusOption[] = useMemo(
    () =>
      STATUS_VALUES.map((value) => ({
        value,
        label: t(`statusVocabulary.${value}`),
        color: STATUS_CSS_VARS[value] ?? 'var(--status-unknown)',
      })),
    [t]
  );

  /*
   * EAINT-916: Segmented-strip picker per Miro feedback. `SegmentedControlAtom`
   * renders the `icon` prop as an `<i>` element inside each label; we use it
   * to hang a coloured status dot on each chip via CSS (see `.checkin-form__
   * status-dot--*` rules in the SCSS). Same atom the day-of-week picker in
   * SchedulesEditor uses — reuse per the Miro annotation flagging the
   * atom "might already exist".
   */
  const statusStripOptions: SegmentedControlOption<StatusValue>[] = useMemo(
    () =>
      STATUS_VALUES.map((value) => ({
        value,
        label: t(`statusVocabulary.${value}`),
        icon: `checkin-form__status-dot checkin-form__status-dot--${value}`,
      })),
    [t]
  );

  const handleSubmit = useCallback(() => {
    if (!selectedStatus) {
      setStatusError(t('statusPopover.checkIn.statusRequired'));
      return;
    }
    if (commentRequired && !comment.trim()) {
      setCommentError(t('statusPopover.checkIn.commentRequiredError'));
      return;
    }
    if (minCommentLength > 0 && comment.trim().length < minCommentLength) {
      setCommentError(t('statusPopover.checkIn.commentTooShort', { count: minCommentLength }));
      return;
    }
    onSubmit(selectedStatus, comment.trim() || undefined);
  }, [selectedStatus, comment, commentRequired, minCommentLength, onSubmit, t]);

  const handleStatusChange = useCallback(
    (value: StatusValue | StatusValue[]) => {
      // SegmentedControl `mode="single"` always emits a scalar; guard anyway.
      const val = Array.isArray(value) ? value[0] : value;
      setSelectedStatus(val ?? null);
      if (statusError) setStatusError('');
    },
    [statusError]
  );

  const handleCommentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setComment(e.target.value);
      if (commentError) setCommentError('');
    },
    [commentError]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    },
    [onCancel]
  );

  const classes = ['checkin-form', className].filter(Boolean).join(' ');

  return (
    <form className={classes} onKeyDown={handleKeyDown} onSubmit={(e) => e.preventDefault()}>
      {/*
       * EAINT-916: "NEW CHECK-IN" eyebrow + status-chip strip per Miro
       * mock. Was a dropdown; a strip surfaces all five status values
       * inline and cuts the click-to-pick to a single tap.
       */}
      <div className="checkin-form__eyebrow">
        {t('statusPopover.checkIn.newCheckInEyebrow')}
      </div>
      <div className="checkin-form__field">
        <SegmentedControlAtom<StatusValue>
          options={statusStripOptions}
          value={selectedStatus ?? ('' as StatusValue)}
          onChange={(v) => handleStatusChange(v)}
          mode="single"
          variant="strip"
          ariaLabel={t('statusPopover.checkIn.statusPlaceholder')}
          errors={statusError ? [statusError] : []}
          data-testid="checkin-form-status-strip"
        />
      </div>

      {/* Velocity Display */}
      {velocityBucket && (
        <div className="checkin-form__velocity">
          <span className="checkin-form__velocity-label">{t('velocity.label')}:</span>
          <span
            className="checkin-form__velocity-badge"
            style={{ color: VELOCITY_BUCKET_COLORS[velocityBucket] || 'var(--neutral-400)' }}
          >
            {t(VELOCITY_BUCKET_LABEL_KEYS[velocityBucket] || 'velocity.buckets.stable')}
            {velocityScore !== undefined && (
              <span className="checkin-form__velocity-score">
                ({velocityScore > 0 ? '+' : ''}{velocityScore})
              </span>
            )}
          </span>
        </div>
      )}

      {/* Comment Textarea */}
      <div className="checkin-form__field">
        <TextareaAtom
          value={comment}
          onChange={handleCommentChange}
          rows={2}
          placeholder={t('statusPopover.checkIn.commentPlaceholder')}
          className="checkin-form__textarea"
          error={Boolean(commentError)}
          errorMessage={commentError}
          disabled={isSubmitting}
          autoResize
        />
      </div>

      {/* API Error */}
      {error && <small className="checkin-form__error">{error}</small>}

      {/* Action Buttons */}
      <div className="checkin-form__actions">
        <ButtonAtom
          type="button"
          variant="text"
          label={t('common.cancel')}
          className="checkin-form__cancel-btn"
          onClick={onCancel}
          disabled={isSubmitting}
          size="small"
        />
        <ButtonAtom
          type="button"
          label={
            isSubmitting
              ? t('statusPopover.checkIn.submittingButton')
              : t('statusPopover.checkIn.submitButton')
          }
          icon={isSubmitting ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
          className="checkin-form__submit-btn"
          onClick={handleSubmit}
          disabled={isSubmitting || !selectedStatus}
          size="small"
        />
      </div>
    </form>
  );
};

export default CheckInFormContent;
